// Declarative "publish my UI context" hook. A page/component calls this to contribute its visible
// `surfaces`, and optionally a `mode`/`pageLabel` override. It dispatches on change only: the caller
// passes a `deps` array (like useEffect) describing when its contribution changed, so we don't churn
// the store on every render.
//
// Only pass keys the page owns (`surfaces`, optional `mode`/`pageLabel`). `path`/`viewport`/`chrome`/
// `rev` are owned by the route tracker + shell. Idempotent under StrictMode (a re-run re-dispatches).
import { useEffect } from 'react';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { setUIContext, type UIContextState } from '@/redux/slices/uiContextSlice';

export function useRegisterUIContext(
  partial: Partial<Pick<UIContextState, 'mode' | 'pageLabel' | 'surfaces'>>,
  deps: React.DependencyList,
): void {
  const dispatch = useAppDispatch();
  useEffect(() => {
    dispatch(setUIContext(partial));
    // The caller controls when `partial` changed via `deps`; we intentionally key the effect on
    // `deps`, not on `partial`'s (churning) object identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, ...deps]);
}
